import { UnitRate } from '@scenario/models/unit-rate.model';
import { ChartCustomizer } from './chart-line-customizer';
import { ChartMarkerConfiguration } from './chart-marker.configuration';
import { ChartMarkers } from '@scenario/enums/chart/chart-markers.enum';
import { getDefaultColorByIndex } from '@configurations/scenario/chart/chart-default-colors';
import { DesignSystemColors } from '@configurations/design-system/design-system-colors.enum';
import { Scenario, getScenarioKey } from '@scenario/models/scenario.model';

export class PriceDatasetsBuilder {
  private readonly chartCustomizer: ChartCustomizer;
  private readonly labels;
  private readonly datasets;
  private highlightIsEnable: boolean = false;
  private readonly historicalColor = '#808080';

  constructor(
    private readonly scenarios: Scenario[],
    private readonly unitRate: UnitRate,
    public readonly chartMarkersConfiguration: ChartMarkerConfiguration,
    private readonly historicalUnitRate: UnitRate = unitRate,
  ) {
    this.chartCustomizer = new ChartCustomizer(chartMarkersConfiguration);
    this.labels = this.yearsToDisplay();
    this.datasets = this.createDataset();
  }

  isHighlightEnable = () => this.highlightIsEnable;
  setHighlightEnable = (highlightIsEnable: boolean) => (this.highlightIsEnable = highlightIsEnable);

  getPointOrIndexMode() {
    if (this.isHighlightEnable()) {
      return 'index';
    } else if (this.datasets.length > 5) {
      return 'point';
    } else {
      return 'index';
    }
  }

  build() {
    return {
      labels: this.labels,
      datasets: this.datasets,
    };
  }

  buildWithHighlight(datasetIndex: number) {
    const limitsDatasets = this.createLimitsDataset();
    return [limitsDatasets[0], this.datasets[datasetIndex], limitsDatasets[1]];
  }

  createDataset = () => {
    const scenariosWithThickness = this.chartCustomizer.processScenariosThickness(this.scenarios);

    return (scenariosWithThickness as Scenario[]).map((scenario, index) => {
      const defaultColor = getDefaultColorByIndex(index);
      const combinedPrices = this.combineWithHistoricalPrices(scenario);
      const isHistoricalDataset = this.isHistoricalDataset(scenario);
      const datasetColor = isHistoricalDataset ? this.historicalColor : defaultColor;
      const historicalMask = this.createHistoricalMask(combinedPrices);
      const defaultBorderDash = this.chartCustomizer.retrieveLineType(scenario);
      const historicalBorderDash = [6, 4];
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      let dataset: any = {
        label: getScenarioKey(scenario),
        data: this.processAllPrices(combinedPrices),
        borderWidth: this.chartCustomizer.retrieveThickness(scenario),
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: (context: { dataIndex: number }) =>
          historicalMask[context.dataIndex] ? this.historicalColor : datasetColor,
        borderDash: defaultBorderDash,
        pointStyle: (context: { dataIndex: number }) =>
          this.chartCustomizer.retrievePointShape(
            scenario,
            historicalMask[context.dataIndex] ? this.historicalColor : datasetColor,
          ),
        borderColor: datasetColor,
        segment: {
          borderColor: (context: { p0DataIndex: number; p1DataIndex: number }) =>
            historicalMask[context.p0DataIndex] && historicalMask[context.p1DataIndex]
              ? this.historicalColor
              : datasetColor,
          borderDash: (context: { p0DataIndex: number; p1DataIndex: number }) =>
            historicalMask[context.p0DataIndex] && historicalMask[context.p1DataIndex]
              ? historicalBorderDash
              : defaultBorderDash,
        },
      };

      if (!isHistoricalDataset && !this.chartCustomizerConfigurationAllowRandomColor()) {
        const referentialColor = this.chartCustomizer.retrieveColor(scenario);
        dataset = {
          ...dataset,
          borderColor: referentialColor,
          pointBackgroundColor: (context: { dataIndex: number }) =>
            historicalMask[context.dataIndex] ? this.historicalColor : referentialColor,
          pointStyle: (context: { dataIndex: number }) =>
            this.chartCustomizer.retrievePointShape(
              scenario,
              historicalMask[context.dataIndex] ? this.historicalColor : referentialColor,
            ),
          segment: {
            borderColor: (context: { p0DataIndex: number; p1DataIndex: number }) =>
              historicalMask[context.p0DataIndex] && historicalMask[context.p1DataIndex]
                ? this.historicalColor
                : referentialColor,
            borderDash: (context: { p0DataIndex: number; p1DataIndex: number }) =>
              historicalMask[context.p0DataIndex] && historicalMask[context.p1DataIndex]
                ? historicalBorderDash
                : defaultBorderDash,
          },
        };
      }

      return dataset;
    });
  };

  chartCustomizerConfigurationAllowRandomColor = (): boolean =>
    this.chartCustomizer.chartMarkerConfiguration.zone === ChartMarkers.NONE &&
    !Object.values(this.chartCustomizer.chartMarkerConfiguration).includes(ChartMarkers.COLOR);

  isHistoricalDataset = (scenario: Scenario): boolean =>
    Object.keys(scenario.pricePerYear).length === 0 &&
    Object.keys(scenario.historicalPricePerYear).length > 0;

  convertPrice = (year: number, price: number | null): number | null => {
    const rate = this.unitRate.ratesByYear[year] ?? 1;
    return price === null ? null : price * rate;
  };

  convertHistoricalPrice = (year: number, price: number | null): number | null => {
    const rate = this.historicalUnitRate.ratesByYear[year] ?? 1;
    return price === null ? null : price * rate;
  };

  combineWithHistoricalPrices = (
    scenario: Scenario,
  ): Record<number, { price: number | null; isHistorical: boolean }> => {
    const result: Record<number, { price: number | null; isHistorical: boolean }> = {};
    for (const [year, price] of Object.entries(scenario.historicalPricePerYear)) {
      result[Number(year)] = { price, isHistorical: true };
    }
    for (const [year, price] of Object.entries(scenario.pricePerYear)) {
      result[Number(year)] = { price, isHistorical: false };
    }
    return result;
  };

  processAllPrices = (
    combinedPrices: Record<number, { price: number | null; isHistorical: boolean }>,
  ): (number | null)[] => {
    return this.labels.map(year => {
      const entry = combinedPrices[year];
      if (entry?.price === null || entry?.price === undefined) return null;
      if (entry.isHistorical) {
        return this.convertHistoricalPrice(year, entry.price);
      }
      return this.convertPrice(year, entry.price);
    });
  };

  yearsToDisplay = () => {
    const yearsToDisplay: number[] = [];
    let minYear: number = 9999999999;
    let maxYear: number = 0;
    this.scenarios.forEach(scenario => {
      const priceYears = Object.keys(scenario.pricePerYear).map(Number);
      const historicalYears = Object.keys(scenario.historicalPricePerYear).map(Number);
      const scenarioYears = [...priceYears, ...historicalYears];
      if (scenarioYears.length === 0) return;
      const min = Math.min(...scenarioYears);

      if (min < minYear) {
        minYear = min;
      }
      const max = Math.max(...scenarioYears);
      if (max > maxYear) {
        maxYear = max;
      }
    });
    for (let currentYear = minYear; currentYear <= maxYear; currentYear++) {
      yearsToDisplay.push(currentYear);
    }
    return yearsToDisplay;
  };

  createHistoricalMask = (
    combinedPrices: Record<number, { price: number | null; isHistorical: boolean }>,
  ): boolean[] => this.labels.map(year => combinedPrices[year]?.isHistorical ?? false);

  createLimitsDataset = () => {
    const maxValues: number[] = [];
    const minValues: number[] = [];
    this.labels.forEach((year, index) => {
      this.datasets.forEach(dataset => {
        const price = dataset.data[index];
        if (price !== null) {
          maxValues[index] = Math.max(maxValues[index] ?? -Infinity, price);
          minValues[index] = Math.min(minValues[index] ?? Infinity, price);
        }
      });
    });

    const shadowColor = `${DesignSystemColors.BLUE_051}22`;

    return [
      {
        label: 'Max',
        data: maxValues,
        borderWidth: 1,
        pointRadius: 1,
        pointHoverRadius: 1,
        borderDash: [0, 0],
        pointStyle: 'circle',
        borderColor: shadowColor,
        backgroundColor: shadowColor,
      },
      {
        label: 'Min',
        data: minValues,
        borderWidth: 1,
        pointRadius: 1,
        pointHoverRadius: 1,
        borderDash: [0, 0],
        pointStyle: 'circle',
        borderColor: shadowColor,
        fill: '-2',
        backgroundColor: shadowColor,
      },
    ];
  };
}
